import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Download, Truck } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { toast } from 'sonner';

const money = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const STATUS_CLS = {
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_approval: 'bg-violet-50 text-violet-700 border-violet-200',
  open: 'bg-red-50 text-red-700 border-red-200',
};

export const DispatchReportPanel = ({ start, end, customerId: fixedCustomerId, hideCustomerSelect = false }) => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(fixedCustomerId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expand, setExpand] = useState(false);
  const [view, setView] = useState('internal');

  useEffect(() => {
    if (hideCustomerSelect) return;   // portal view doesn't need the dropdown
    api.get('/customers').then((r) => setCustomers(r.data.filter((c) => c.active !== false)))
      .catch(() => toast.error('Could not load customers'));
  }, [hideCustomerSelect]);

  // Keep local id in sync with the prop for portal users (fixed customer).
  useEffect(() => {
    if (fixedCustomerId) setCustomerId(fixedCustomerId);
  }, [fixedCustomerId]);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const r = await api.get('/reports/dispatch', {
        params: { customer_id: customerId, start, end, expand, view },
      });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [customerId, start, end, expand, view]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const r = await api.get('/reports/dispatch/export', {
        params: { fmt, customer_id: customerId, start, end, expand, view },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dispatch${expand ? '-by-contractor' : ''}${view === 'client' ? '-client-copy' : ''}-${(data?.customer_name || 'report').replace(/\s+/g, '-').toLowerCase()}-${start}-to-${end}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="dispatch-report-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Truck className="w-4 h-4 text-[#0D5B69]" /> Client Dispatch Report
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {!hideCustomerSelect && (
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="w-64" data-testid="dispatch-customer-select">
                  <SelectValue placeholder="Select a customer…" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {['xlsx', 'csv', 'pdf'].map((fmt) => (
              <Button
                key={fmt} variant="outline" size="sm" className="gap-1.5"
                disabled={!customerId || exporting || !data?.rows?.length}
                onClick={() => handleExport(fmt)}
                data-testid={`dispatch-export-${fmt}`}
              >
                <Download className="w-3.5 h-3.5" /> {fmt.toUpperCase()}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-5 mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Switch
              id="dispatch-expand"
              checked={expand}
              onCheckedChange={setExpand}
              data-testid="dispatch-expand-toggle"
            />
            <Label htmlFor="dispatch-expand" className="text-xs text-slate-600 cursor-pointer">
              Break down by contractor
            </Label>
          </div>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="h-8 w-[230px] text-xs" data-testid="dispatch-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="internal">Internal — cost &amp; margin</SelectItem>
              <SelectItem value="client">Client copy — charge only</SelectItem>
            </SelectContent>
          </Select>
          {view === 'client' && (
            <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Provider cost and margin are removed from this copy.
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {expand
            ? 'One row per contractor leg — the occurrence ref repeats, and an OCCURRENCE TOTAL row closes off any case that had more than one contractor.'
            : 'One row per incident for the selected customer — end-client, times and invoice value (ex VAT).'}
          {' '}Uses the date range selected above.
        </p>
      </CardHeader>
      <CardContent>
        {!customerId ? (
          <p className="text-sm text-slate-400 py-10 text-center" data-testid="dispatch-empty-state">
            Select a customer to generate their dispatch report.
          </p>
        ) : loading ? (
          <p className="text-sm text-slate-400 py-10 text-center">Loading…</p>
        ) : !data?.rows?.length ? (
          <p className="text-sm text-slate-400 py-10 text-center" data-testid="dispatch-no-rows">
            No incidents for {data?.customer_name} in this period.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-4 mb-4 text-sm" data-testid="dispatch-totals">
              <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
                <span className="text-slate-500">Occurrences:</span>{' '}
                <span className="font-semibold">{data.totals.incidents}</span>
              </div>
              <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
                <span className="text-slate-500">Invoice value (ex VAT):</span>{' '}
                <span className="font-semibold text-[#0D5B69]">{money(data.totals.invoice_value)}</span>
              </div>
              {data.include_cost && (
                <>
                  <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
                    <span className="text-slate-500">Provider cost:</span>{' '}
                    <span className="font-semibold">{money(data.totals.provider_cost)}</span>
                  </div>
                  <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
                    <span className="text-slate-500">Margin:</span>{' '}
                    <span className="font-semibold text-emerald-700">{money(data.totals.margin)}</span>
                  </div>
                </>
              )}
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {data.expanded && <TableHead>Occurrence #</TableHead>}
                    <TableHead>Date</TableHead>
                    <TableHead>Registration</TableHead>
                    <TableHead>Client Name/Ref#</TableHead>
                    <TableHead>Area</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Ref Number</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead className="text-right">Invoice (R)</TableHead>
                    <TableHead>Provider</TableHead>
                    {data.include_cost && <TableHead className="text-right">Cost (R)</TableHead>}
                    {data.include_cost && data.expanded && <TableHead className="text-right">Margin (R)</TableHead>}
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((r, idx) => {
                    const isSub = r.row_type === 'subtotal';
                    return (
                      <TableRow
                        key={`${r.incident_id}-${r.row_type}-${r.leg_index || 0}`}
                        className={isSub ? 'bg-slate-50 font-semibold' : undefined}
                        data-testid={isSub ? `dispatch-subtotal-${idx}` : `dispatch-row-${idx}`}
                      >
                        {data.expanded && (
                          <TableCell className="font-mono text-[11px]">{r.incident_no}</TableCell>
                        )}
                        <TableCell className="whitespace-nowrap text-xs">{isSub ? '' : r.date}</TableCell>
                        <TableCell className="font-mono text-xs">{isSub ? '' : (r.registration || '—')}</TableCell>
                        <TableCell className="text-xs font-medium">{isSub ? '' : (r.end_client || '—')}</TableCell>
                        <TableCell className="text-xs max-w-[160px] truncate" title={r.area}>{isSub ? '' : (r.area || '—')}</TableCell>
                        <TableCell className={`text-xs ${isSub ? 'text-slate-700' : ''}`}>
                          {r.event || '—'}
                          {r.leg_count > 1 && !isSub && (
                            <span className="ml-1 text-[10px] text-slate-400">leg {r.leg_index}/{r.leg_count}</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-[11px]">{isSub ? '' : r.ref}</TableCell>
                        <TableCell className="text-xs">{r.start_time || '—'}</TableCell>
                        <TableCell className="text-xs">{r.end_time || '—'}</TableCell>
                        <TableCell className="text-xs">{r.total_time || '—'}</TableCell>
                        <TableCell className="text-right text-xs font-semibold">{money(r.invoice_value)}</TableCell>
                        <TableCell className="text-xs max-w-[140px] truncate" title={r.service_provider}>{r.service_provider || '—'}</TableCell>
                        {data.include_cost && (
                          <TableCell className="text-right text-xs">{money(r.provider_cost)}</TableCell>
                        )}
                        {data.include_cost && data.expanded && (
                          <TableCell className="text-right text-xs text-emerald-700">{money(r.margin)}</TableCell>
                        )}
                        <TableCell>
                          {!isSub && (
                            <Badge className={`text-[10px] border ${STATUS_CLS[r.status] || ''} hover:opacity-100`}>
                              {(r.status || '').replace('_', ' ')}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
